import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Users } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchRelatedAccounts, type AdminRelatedAccount } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A3.21 — Bog'liq akkauntlar. GET /admin/users/:id/related — bir QURILMANI
 * (device_uid) baham ko'radigan boshqa akkauntlar (multi-akkaunt anti-fraud
 * signali). IP bilan bog'lanmaydi (proksi hammani bitta IP'ga yig'adi → soxta
 * bog'lanish bo'lardi). Endpoint (lane-A patch) deploy bo'lgach jonli — hozircha
 * halol empty-state (404).
 */
export function UserRelated() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminRelatedAccount>[]>(
    () => [
      {
        key: 'user',
        header: t.col.name,
        type: 'text',
        minWidth: 160,
        sortable: false,
        render: (r) => {
          const label =
            [r.first_name, r.last_name].filter(Boolean).join(' ') ||
            (r.username ? `@${r.username}` : `#${r.user_id}`)
          return (
            <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
              {label}
            </Link>
          )
        },
      },
      { key: 'username', header: t.col.username, type: 'text', width: 140 },
      { key: 'platform', header: t.userExtra.platform, type: 'text', width: 110 },
      { key: 'device_model', header: t.userExtra.device, type: 'text', minWidth: 140 },
      { key: 'last_active_at', header: t.userExtra.lastActive, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchRelatedAccounts(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.userExtra.relatedTitle}
        screenId="A3.21"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminRelatedAccount>
        screenId="A3.21"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => `${r.user_id}:${r.device_uid}`}
        exportable
        emptyState={{ icon: <Users size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
